import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CancelReason } from 'server/shared/models/cancelReason';
import { Customer } from 'server/shared/models/customer';
import {
  CreateEmploymentArgs,
  Employment,
  UpdateEmploymentArgs,
  UpdateEmploymentTransferArgs,
} from 'server/shared/models/employment';
import {
  EmploymentInviteeStatusEnum,
  EmploymentInviteeStatusReason,
} from 'server/shared/models/employmentInviteeStatusEnum';
import { EmploymentTransfer } from 'server/shared/models/employmentTransferConfig';
import { PreparedEmploymentPoolerUser } from '../../types/employment/PreparedEmploymentPoolerUser';
import { CancelDialog } from '../jobs/job-edit/dialogs/cancel-dialog/cancel-dialog.component';
import { CustomerService } from './customer.service';
import { ToastService } from './toast/toast.service';

@Injectable({
  providedIn: 'root',
})
export class EmploymentService {
  constructor(
    private toastService: ToastService,
    private dialog: MatDialog,
    private router: Router,
    private customerService: CustomerService,
  ) {}

  async inviteUsersToEmployment(
    employmentId: string,
    users: PreparedEmploymentPoolerUser[],
  ) {
    return this.runMethod('inviteUsersToEmployment', employmentId, users);
  }

  async updateEmployment(updateProps: UpdateEmploymentArgs) {
    return this.runMethod('updateEmployment', updateProps);
  }

  async updateEmploymentTransfer(updateProps: UpdateEmploymentTransferArgs) {
    return this.runMethod('updateEmploymentTransfer', updateProps);
  }

  async createEmployment(createProps: CreateEmploymentArgs) {
    return this.runMethod('createEmployment', createProps);
  }

  async hideEmploymentForPooler(employmentId: string) {
    return this.runMethod('hideEmploymentForPooler', employmentId);
  }

  async updateEmploymentInviteeStatus(
    inviteeId: string,
    employmentId: string,
    status: EmploymentInviteeStatusEnum,
    statusInfo?: EmploymentInviteeStatusReason,
  ) {
    return this.runMethod(
      'updateEmploymentInviteeStatus',
      inviteeId,
      employmentId,
      status,
      statusInfo,
    );
  }

  async answerEmploymentInvitation(
    employmentId: string,
    inviteeId: string,
    status:
      | EmploymentInviteeStatusEnum.AVAILABLE
      | EmploymentInviteeStatusEnum.UNAVAILABLE,
  ) {
    return this.runMethod(
      'answerEmploymentInvitation',
      employmentId,
      inviteeId,
      status,
    );
  }

  async employmentSelfSignIn(
    employmentId: string,
    desiredCustomer: Customer,
    response:
      | EmploymentInviteeStatusEnum.AVAILABLE
      | EmploymentInviteeStatusEnum.UNAVAILABLE,
  ) {
    return this.runMethod(
      'employmentSelfSignIn',
      employmentId,
      desiredCustomer,
      response,
    );
  }

  /**
   * Cancel employment dialog can be opened from the List or the detail view,
   * which is why it is here in the service
   */
  async cancelEmployment(employmentId: string, cancelReasons: CancelReason[]) {
    const dialogRef = this.dialog.open(CancelDialog, {
      data: {
        reasons: cancelReasons,
      },
    });
    dialogRef
      .afterClosed()
      .subscribe(
        async (dialogData: {
          reason: CancelReason;
          reasonDescription: string;
          informInviteeOfReason: boolean;
        }) => {
          if (dialogData && dialogData.reason) {
            const result = await this.runMethod('cancelEmployment', {
              employmentId,
              reason: dialogData.reason,
              reasonDescription: dialogData.reasonDescription,
              informInviteeOfReason: dialogData.informInviteeOfReason,
            });
            this.toastService.add({
              type: 'SUCCESS',
              message: 'employmentCanceled',
            });
            return result;
          }
        },
      );
  }

  async runMethod(methodName: string, ...args: any[]) {
    return new Promise<any>((resolve, reject) => {
      Meteor.call(methodName, ...args, (error: Meteor.Error, result: any) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
          reject();
        } else {
          resolve(result);
        }
      });
    });
  }

  async duplicateJob(employment: Employment) {
    const customer = await this.customerService.dispoCustomer$.value;

    const queryParams: any = {
      departmentId: employment.department.id,
      // EditorId is intended to be the current user and not duplicated
      // editorId: employment.editor.userId,
      workload: employment.workload,
      certificates: employment.certificates.map((c) => c.id).join(','),
      specialities: employment.specialities.map((s) => s.id).join(','),
      sex: employment.sex,
      car: employment.car,
      language: employment.language,
      description: employment.description
        ? encodeURIComponent(employment.description)
        : undefined,
    };
    if (customer?.enableCopyDateOnDuplicate) {
      queryParams.startDate = employment.startDate;
      queryParams.endDate = employment.endDate;
    }
    await this.router.navigate(['/temp-work/employments/new'], {
      queryParams,
    });
  }

  async transferEmployment(employmentId: string, transfer: EmploymentTransfer) {
    return this.runMethod('transferEmployment', employmentId, transfer);
  }
  async transferEmploymentToAll(employmentId: string) {
    return this.runMethod('transferEmploymentToAll', employmentId);
  }
}
